import { useState } from 'react';
import { HelpCircle, X, BookOpen } from 'lucide-react';
import { GLOSSARY } from '../lib/glossary';

/**
 * HelpDot — ícono (?) pequeño que abre un modal educativo con la definición
 * del término, en lenguaje pre-escolar, con ejemplos concretos.
 *
 * Uso:
 *   <HelpDot termKey="rat" />                 // usa GLOSSARY.rat
 *   <HelpDot termKey="rat" size="sm" />       // versión más pequeña
 *   <HelpDot termKey="rat" label="¿Qué es?" /> // con texto
 */
export default function HelpDot({ termKey, size = 'md', label = null }) {
  const [open, setOpen] = useState(false);
  const entry = GLOSSARY[termKey];
  if (!entry) return null;

  const dim = size === 'sm' ? 12 : size === 'lg' ? 16 : 14;
  const cls = size === 'sm'
    ? 'w-4 h-4 text-[11px]'
    : size === 'lg' ? 'w-6 h-6 text-[13px]' : 'w-5 h-5 text-[12px]';

  return (
    <>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        className={`inline-flex items-center gap-1 ${cls} rounded-full border border-[#D1D5DB] bg-white text-[#6B7280] hover:border-[#143A34] hover:text-[#143A34] hover:bg-[#F0FDF4] transition-colors align-middle shrink-0`}
        title={`¿Qué es "${entry.term}"?`}
        aria-label={`Explicación de ${entry.term}`}
        data-testid={`helpdot-${termKey}`}
      >
        {label ? (
          <span className="px-2 py-0.5 whitespace-nowrap flex items-center gap-1">
            <HelpCircle size={dim} strokeWidth={2} /> {label}
          </span>
        ) : (
          <HelpCircle size={dim} strokeWidth={2} className="m-auto" />
        )}
      </button>

      {open && (
        <div className="fixed inset-0 bg-black/40 z-[60] flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div
            className="bg-white border border-[#E5E7EB] max-w-xl w-full max-h-[90vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
            data-testid={`helpdot-modal-${termKey}`}
          >
            <div className="px-6 py-4 border-b border-[#E5E7EB] flex items-start justify-between gap-4">
              <div className="flex items-start gap-2 min-w-0">
                <BookOpen size={17} className="text-[#143A34] mt-0.5 shrink-0" strokeWidth={1.75} />
                <div className="min-w-0">
                  <div className="text-[15px] font-semibold text-[#1C2329] leading-snug">{entry.term}</div>
                  {entry.law && (
                    <div className="text-[11px] text-[#6B7280] mt-0.5 font-mono">{entry.law}</div>
                  )}
                </div>
              </div>
              <button onClick={() => setOpen(false)} className="p-1.5 rounded hover:bg-[#F3F4F6] text-[#6B7280] shrink-0" aria-label="Cerrar">
                <X size={16} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5 text-[13.5px] leading-relaxed text-[#374151]">
              <Section title="¿Qué es?">
                {entry.what}
              </Section>
              <Section title="¿Para qué sirve?">
                {entry.why}
              </Section>
              <Section title="¿Cómo se llena o se hace?">
                {entry.how}
              </Section>
              {entry.examples && entry.examples.length > 0 && (
                <Section title="Ejemplos concretos">
                  <ul className="mt-1 space-y-1.5">
                    {entry.examples.map((ex, i) => (
                      <li key={i} className="flex gap-2">
                        <span className="text-[#2A9B68] font-mono shrink-0">·</span>
                        <span>{ex}</span>
                      </li>
                    ))}
                  </ul>
                </Section>
              )}
              {entry.notApply && (
                <Section title="Cuándo NO aplica">
                  {entry.notApply}
                </Section>
              )}
            </div>

            <div className="px-6 py-3 border-t border-[#E5E7EB] bg-[#FAFBFC] text-[11.5px] text-[#6B7280]">
              Definición pensada para gerentes y responsables sin formación jurídica.
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-[10.5px] tracking-wider text-[#6B7280] font-medium uppercase mb-1.5">
        {title}
      </div>
      <div className="text-[#1C2329]">
        {children}
      </div>
    </div>
  );
}
